import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, MapPin, Star } from "lucide-react";
import { api } from "../lib/api";
import BbiShell from "../components/BbiShell";

export default function BrandWall() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    document.title = "All Brands — BrandsByIndia | Verified Indian D2C Brands";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Browse all verified Indian D2C brands on BrandsByIndia — fashion, beauty, jewellery and more with stories, store links and buying guides.");
    api.get("/public/brands").then(({ data }) => {
      setBrands(data.brands || []);
      setCats(data.categories || []);
    }).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    const Q = q.trim().toLowerCase();
    return brands.filter((b) =>
      (cat === "All" || b.category === cat) &&
      (!Q || b.name.toLowerCase().includes(Q) || (b.tagline || "").toLowerCase().includes(Q) || (b.keywords || "").toLowerCase().includes(Q))
    );
  }, [brands, q, cat]);

  return (
    <BbiShell>
      <section className="pt-14 sm:pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h1 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter text-white">
            The <span className="mkt-gradient-text-vivid">Brand Wall.</span>
          </h1>
          <p className="text-sm sm:text-base text-slate-400 mt-3 max-w-2xl">
            Every verified Indian brand on BrandsByIndia — bigger boxes mean higher visibility.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="brand-directory-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search brands…"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400/50"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {["All", ...cats.slice(0, 5)].map((c) => (
                <button
                  key={c}
                  data-testid={`brand-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setCat(c)}
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors ${
                    cat === c ? "bg-indigo-500 border-indigo-400 text-white" : "bg-white/[0.03] border-white/10 text-slate-400 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {filtered.length === 0 ? (
            <div data-testid="brand-directory-empty" className="text-slate-500 text-sm py-16 text-center border border-dashed border-white/10 rounded-2xl">
              {brands.length === 0 ? "Brands launching soon — check back shortly." : "No brands match your search."}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((b) => {
                const featured = (b.boxes || 1) >= 3;
                return (
                <Link
                  key={b.slug}
                  to={`/brands/${b.slug}`}
                  data-testid={`brand-card-${b.slug}`}
                  className={`group border rounded-2xl p-6 transition-all ${
                    featured
                      ? "sm:col-span-2 bg-gradient-to-br from-indigo-500/15 to-fuchsia-500/10 border-indigo-400/40 hover:border-indigo-300/60"
                      : "bg-white/[0.03] border-white/10 hover:border-indigo-400/40 hover:bg-white/[0.05]"
                  }`}
                >
                  {featured && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-300 text-[10px] font-bold uppercase tracking-wider mb-4">
                      <Star className="w-3 h-3" /> Featured
                    </span>
                  )}
                  <div className="flex items-center gap-4">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt={`${b.name} logo`} className={`rounded-xl object-cover bg-white ${featured ? "w-16 h-16" : "w-14 h-14"}`} />
                    ) : (
                      <div className={`rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center text-white font-bold ${featured ? "w-16 h-16 text-2xl" : "w-14 h-14 text-xl"}`}>
                        {b.name[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className={`text-white font-semibold truncate ${featured ? "text-lg" : ""}`}>{b.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-indigo-400 font-bold mt-0.5">{b.category}</div>
                    </div>
                  </div>
                  {b.tagline && <p className="text-sm text-slate-400 mt-4 line-clamp-2 leading-relaxed">{b.tagline}</p>}
                  <div className="flex items-center justify-between mt-5">
                    {b.city ? (
                      <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{b.city}</span>
                    ) : <span />}
                    <span className="text-xs text-indigo-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      View brand <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              );})}
            </div>
          )}

          <div className="mt-14 bg-gradient-to-r from-indigo-500/10 to-fuchsia-500/10 border border-indigo-400/20 rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <div className="text-white font-semibold text-lg">Want your brand on this wall?</div>
              <p className="text-sm text-slate-400 mt-1">Verified brand page + AI SEO articles + customer enquiries — ₹999/month incl. GST.</p>
            </div>
            <Link
              to="/brands/pricing"
              data-testid="brand-wall-cta"
              className="px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-semibold transition-colors whitespace-nowrap"
              style={{ color: "#FFFFFF" }}
            >
              Get Listed — ₹999/mo
            </Link>
          </div>
        </div>
      </section>
    </BbiShell>
  );
}
